import { API_BASE_URL } from '@/config/api';
import { apiService } from '@/services/apiService';

export interface BraceletGiftProduct {
  productId: string;
  productName: string;
  imageUrl: string;
  appPrice: number;
  originalPrice?: number;
  category?: string;
  categorySlug?: string;
  productSlug?: string;
}

export interface BraceletGiftSettings {
  enabled: boolean;
  eligibleProductIds: string[];
  braceletGiftProductIds: string[];
  malaGiftProductIds: string[];
  eligibleProducts: BraceletGiftProduct[];
  braceletGifts: BraceletGiftProduct[];
  malaGifts: BraceletGiftProduct[];
  startsAt: number;
  expiresAt: number;
}

export type BraceletGiftDraft = Pick<BraceletGiftSettings,
  'enabled' | 'eligibleProductIds' | 'braceletGiftProductIds' | 'malaGiftProductIds' | 'startsAt' | 'expiresAt'>;

export interface CartBraceletGift {
  active: boolean;
  eligibleCount: number;
  tier: 0 | 2 | 5;
  selectedGiftProductId?: string | null;
  giftType?: string | null;
  giftSelectionRequired: boolean;
  giftOptions: BraceletGiftProduct[];
}

const unwrap = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
    const envelope = payload as { success: boolean; data: T; error?: string };
    if (!envelope.success) throw new Error(envelope.error || 'Unable to load bracelet gift offer');
    return envelope.data;
  }
  return payload as T;
};

const readError = async (response: Response, fallback: string) => {
  const body = await response.json().catch(() => null);
  return body?.error || body?.message || fallback;
};

export const fetchBraceletGift = async (): Promise<BraceletGiftSettings> => {
  const response = await fetch(`${API_BASE_URL}/read/v1/service/bracelet-gift`);
  if (!response.ok) throw new Error(await readError(response, 'Unable to load bracelet gift offer'));
  return unwrap<BraceletGiftSettings>(await response.json());
};

export const fetchAdminBraceletGift = async (): Promise<BraceletGiftSettings> => {
  const response = await fetch(`${API_BASE_URL}/api/admin/bracelet-gift`, {
    headers: { token: apiService.getToken() || '' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(await readError(response, 'Unable to load promotion settings'));
  return unwrap<BraceletGiftSettings>(await response.json());
};

export const saveAdminBraceletGift = async (draft: BraceletGiftDraft): Promise<BraceletGiftSettings> => {
  const response = await fetch(`${API_BASE_URL}/api/admin/bracelet-gift`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', token: apiService.getToken() || '' },
    body: JSON.stringify(draft),
  });
  if (!response.ok) throw new Error(await readError(response, 'Unable to save promotion settings'));
  return unwrap<BraceletGiftSettings>(await response.json());
};

export const selectBraceletGift = async (productId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/cart/bracelet-gift`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', token: apiService.getToken() || '' },
    body: JSON.stringify({ productId }),
  });
  if (!response.ok) throw new Error(await readError(response, 'Unable to select this gift'));
};

export const clearBraceletGift = async (): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/cart/bracelet-gift`, {
    method: 'DELETE',
    headers: { token: apiService.getToken() || '' },
  });
  if (!response.ok) throw new Error(await readError(response, 'Unable to remove this gift'));
};
